import { useCallback, useRef, useState } from 'react';
import {
  PRODUCT_AVAILABILITY,
  hasProductStatus,
  setProductAvailability,
} from './composeProductAvailability';

// What the days of the timeline report up to their frame and to the footer:
// whether a product can be booked, where the frame's global services band goes,
// and how the frame header removes its experience.
export default function useComposeTimelineRegistry() {
  // A program that cannot be booked — unpublished, not priceable on these
  // dates, or in a currency the trip cannot add up — is known to the day that
  // ran the pricing request, not here. So each instance reports the verdict it
  // draws, and the trip stays unbookable while one of them stands.
  //
  // KD-3643 §2 — hotels answer the same way, in their own registry: a stay is
  // keyed by its first day, a program instance by its first day too, and the two
  // numbering schemes would collide in a single map.
  const [unavailableFrames, setUnavailableFrames] = useState({});
  const [unavailableHotels, setUnavailableHotels] = useState({});

  const registerExperienceWarning = useCallback(
    (startDayIndex, status) =>
      setProductAvailability(setUnavailableFrames, startDayIndex, status),
    [],
  );

  const registerHotelWarning = useCallback(
    (startDayIndex, status) =>
      setProductAvailability(setUnavailableHotels, startDayIndex, status),
    [],
  );

  const hasUnavailableExperience = hasProductStatus(
    unavailableFrames,
    PRODUCT_AVAILABILITY.UNAVAILABLE,
  );
  const hasUnavailableHotel = hasProductStatus(
    unavailableHotels,
    PRODUCT_AVAILABILITY.UNAVAILABLE,
  );

  // KD-3643 §3 — a product whose price or availability is still being fetched
  // has not cleared anything yet. Blocking on it closes the window where the
  // button was live for a second or two before the answer landed, and it says
  // nothing about the product: no warning is drawn, only the wait is explained.
  const isAvailabilityPending =
    hasProductStatus(unavailableFrames, PRODUCT_AVAILABILITY.PENDING) ||
    hasProductStatus(unavailableHotels, PRODUCT_AVAILABILITY.PENDING);

  // KD-3581 AC03 — every frame lends a DOM slot after its last day; the day
  // that owns the global selection renders its band into it. The node is kept
  // in state because the day needs the element itself, and the ref callbacks
  // are cached per frame so React does not detach and reattach them on every
  // render.
  const [globalServicesSlots, setGlobalServicesSlots] = useState({});
  const globalServicesSlotRefs = useRef(new Map());

  const getGlobalServicesSlotRef = useCallback(startDayIndex => {
    const refs = globalServicesSlotRefs.current;

    if (!refs.has(startDayIndex)) {
      refs.set(startDayIndex, node =>
        setGlobalServicesSlots(previous =>
          previous[startDayIndex] === node
            ? previous
            : { ...previous, [startDayIndex]: node },
        ),
      );
    }

    return refs.get(startDayIndex);
  }, []);

  // KD-3581 §3 — the removal cross sits in the frame header, but the removal
  // itself (and its required-options warning) belongs to the day. Each frame's
  // first day registers its handler here; `removableFrames` mirrors the
  // registry in state so the header knows whether to draw the cross at all.
  const removeExperienceHandlers = useRef(new Map());
  const [removableFrames, setRemovableFrames] = useState({});

  const registerRemoveHandler = useCallback((startDayIndex, handler) => {
    if (handler) {
      removeExperienceHandlers.current.set(startDayIndex, handler);
      setRemovableFrames(previous =>
        previous[startDayIndex]
          ? previous
          : { ...previous, [startDayIndex]: true },
      );
      return;
    }

    removeExperienceHandlers.current.delete(startDayIndex);
    setRemovableFrames(previous => {
      if (!previous[startDayIndex]) {
        return previous;
      }

      const next = { ...previous };
      delete next[startDayIndex];
      return next;
    });
  }, []);

  return {
    registerExperienceWarning,
    registerHotelWarning,
    hasUnavailableExperience,
    hasUnavailableHotel,
    isAvailabilityPending,
    hasUnbookableProduct:
      hasUnavailableExperience || hasUnavailableHotel || isAvailabilityPending,
    globalServicesSlots,
    getGlobalServicesSlotRef,
    removableFrames,
    registerRemoveHandler,
    removeFrameExperience: startDayIndex =>
      removeExperienceHandlers.current.get(startDayIndex)?.(),
  };
}
